import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, Copy, Clock, Check, Pencil, Instagram, Facebook, Linkedin, Twitter, Search, Loader2, X, ImagePlus, Upload, Wand2, Send } from 'lucide-react';
import { LintBadge, HighlightedCopy } from './LintBadge';
import ClaimAudit from './ClaimAudit';
import SocialDrafts from './SocialDrafts';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

const ANGLES = [
  'Standard HG Voice',
  'Bold/Provocative',
  'Educational/Fitting Tip',
  'Behind the Scenes',
  'Product/Launch',
  'News Reflection (Industry Echo)',
];

const AUDIENCE_OPTIONS = [
  { id: 'mixed',   label: 'Mixed (Golfers + Fitters)' },
  { id: 'golfers', label: 'Golfers (end players)' },
  { id: 'fitters', label: 'Fitters & Teaching Pros' },
  { id: 'trade',   label: 'Trade & Industry' },
];

const STORY_SHAPE_OPTIONS = [
  { id: 'auto',             label: 'Auto (pick best fit)' },
  { id: 'industry_echo',    label: 'Industry Echo (news → HG worldview)' },
  { id: 'fitting_moment',   label: 'Fitting Moment' },
  { id: 'coach_spotlight',  label: 'Coach Spotlight' },
  { id: 'component_story',  label: 'Component Story' },
  { id: 'industry_take',    label: 'Industry Take' },
  { id: 'junior_story',     label: 'Junior Story' },
  { id: 'built_in_idaho',   label: 'Built-in-Idaho' },
];

function ImagePanel({ postIndex, topic, platform, imageData, onImageSet }) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const handleGenerateImage = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await axios.post(`${API}/social/generate-image`, {
        prompt: topic || 'premium golf equipment and fitting',
        platform,
      });
      onImageSet(res.data.image_data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Image generation failed');
    }
    setGenerating(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/social/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onImageSet(res.data.image_data);
    } catch (err) {
      setError('Upload failed');
    }
  };

  return (
    <div className="space-y-3">
      {imageData ? (
        <div className="relative group">
          <img
            data-testid={`post-image-${postIndex}`}
            src={imageData}
            alt="Post image"
            className="w-full h-48 object-cover rounded-sm border border-hg-border"
          />
          <button
            onClick={() => onImageSet(null)}
            className="absolute top-2 right-2 bg-black/70 text-hg-text p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="border border-dashed border-hg-border rounded-sm p-4 space-y-3">
          <div className="text-xs font-sans text-hg-text-dim uppercase tracking-wider text-center">Add Image</div>
          <div className="flex gap-2 justify-center">
            <button
              data-testid={`post-gen-image-${postIndex}`}
              onClick={handleGenerateImage}
              disabled={generating}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
            >
              {generating ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
              {generating ? 'Generating...' : 'AI Generate'}
            </button>
            <label
              data-testid={`post-upload-image-${postIndex}`}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green transition-colors cursor-pointer"
            >
              <Upload size={12} /> Upload
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
            </label>
          </div>
          {generating && (
            <p className="text-[10px] text-hg-text-dim text-center font-sans">This may take up to 30 seconds...</p>
          )}
        </div>
      )}
      {error && <p className="text-xs text-red-400 font-sans">{error}</p>}
    </div>
  );
}

function PostCard({ post, index, topic, platform, channels, onSave, onCopy, onSchedule, onPostNow }) {
  const [editing, setEditing] = useState(false);
  const [editedCopy, setEditedCopy] = useState(post.copy);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [schedDate, setSchedDate] = useState('');
  const [schedTime, setSchedTime] = useState('09:00');
  const [imageData, setImageData] = useState(null);
  // Default-select channels that match the post's platform
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [userTouchedSelection, setUserTouchedSelection] = useState(false);
  const [posting, setPosting] = useState(false);

  // Auto-select channels whose service matches the current platform.
  // Skips if the user has already manually adjusted selections.
  useEffect(() => {
    if (!channels?.length) return;
    if (userTouchedSelection) return;
    setSelectedChannels(
      channels.filter((c) => c.service?.toLowerCase() === platform?.toLowerCase()).map((c) => c.id)
    );
  }, [channels, platform, userTouchedSelection]);

  const toggleChannel = (id) => {
    setUserTouchedSelection(true);
    setSelectedChannels((curr) => curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id]);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(editing ? editedCopy : post.copy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopy && onCopy();
  };

  const handleSave = () => {
    onSave({ ...post, copy: editing ? editedCopy : post.copy }, imageData);
    setSaved(true);
  };

  const handleSchedule = async () => {
    if (selectedChannels.length === 0) return;
    setPosting(true);
    await onSchedule({ ...post, copy: editing ? editedCopy : post.copy }, schedDate, schedTime, imageData, selectedChannels);
    setShowSchedule(false);
    setPosting(false);
  };

  const handlePostNow = async () => {
    if (selectedChannels.length === 0) return;
    setPosting(true);
    await onPostNow({ ...post, copy: editing ? editedCopy : post.copy }, imageData, selectedChannels);
    setShowSchedule(false);
    setPosting(false);
  };

  return (
    <div
      data-testid={`post-card-${index}`}
      className={`bg-hg-surface border rounded-sm p-5 space-y-4 transition-colors ${
        saved ? 'border-hg-green/40' : 'border-hg-border'
      }`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-sans font-medium uppercase tracking-wider text-hg-green">{post.angle}</span>
        {saved && (
          <span className="flex items-center gap-1 text-xs text-hg-green">
            <Check size={12} /> Approved
          </span>
        )}
      </div>

      <ImagePanel
        postIndex={index}
        topic={topic}
        platform={platform}
        imageData={imageData}
        onImageSet={setImageData}
      />

      {editing ? (
        <textarea
          data-testid={`post-edit-textarea-${index}`}
          value={editedCopy}
          onChange={(e) => setEditedCopy(e.target.value)}
          className="w-full bg-hg-bg border border-hg-border rounded-sm p-3 text-sm text-hg-text font-sans resize-none focus:outline-none focus:ring-1 focus:ring-hg-green min-h-[120px]"
          rows={6}
        />
      ) : post.lint && !post.lint.clean ? (
        <HighlightedCopy
          text={post.copy}
          violations={post.lint.violations || []}
          className="text-sm text-hg-text/90 font-sans leading-relaxed whitespace-pre-line"
        />
      ) : (
        <p className="text-sm text-hg-text/90 font-sans leading-relaxed whitespace-pre-line">{post.copy}</p>
      )}

      {post.lint && (
        <div data-testid={`post-lint-${index}`}>
          <LintBadge lint={post.lint} testid={`post-lint-badge-${index}`} />
        </div>
      )}

      <ClaimAudit
        text={editing ? editedCopy : post.copy}
        onTextChange={(newText) => { setEditedCopy(newText); setEditing(true); }}
        testid={`post-${index}-audit`}
      />

      <p className="text-xs text-hg-text-dim italic font-sans">{post.note}</p>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-hg-border">
        <button
          data-testid={`post-edit-btn-${index}`}
          onClick={() => setEditing(!editing)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green transition-colors"
        >
          <Pencil size={12} strokeWidth={1.5} /> {editing ? 'Done' : 'Edit'}
        </button>
        <button
          data-testid={`post-copy-btn-${index}`}
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green transition-colors"
        >
          {copied ? <Check size={12} /> : <Copy size={12} strokeWidth={1.5} />} {copied ? 'Copied' : 'Copy'}
        </button>
        <button
          data-testid={`post-schedule-btn-${index}`}
          onClick={() => setShowSchedule(!showSchedule)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border rounded-sm transition-colors ${
            showSchedule
              ? 'border-hg-green bg-hg-green/10 text-hg-green'
              : 'border-hg-border text-hg-text-dim hover:border-hg-green hover:text-hg-green'
          }`}
        >
          <Clock size={12} strokeWidth={1.5} /> {showSchedule ? 'Close Schedule' : 'Schedule to Buffer'}
        </button>
        <button
          data-testid={`post-approve-btn-${index}`}
          onClick={handleSave}
          disabled={saved}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans rounded-sm transition-colors ${
            saved
              ? 'bg-hg-green/20 text-hg-green border border-hg-green/30 cursor-default'
              : 'bg-hg-green text-hg-text hover:bg-hg-green-hover'
          }`}
        >
          <Check size={12} /> {saved ? 'Saved' : 'Approve & Save'}
        </button>
      </div>

      {showSchedule && (
        <div data-testid={`schedule-panel-${index}`} className="bg-hg-bg border border-hg-border rounded-sm p-4 space-y-3">
          <div className="text-xs font-sans text-hg-text-dim uppercase tracking-wider">Buffer — Pick Channels & Schedule</div>

          {/* Channel multi-select */}
          {channels && channels.length > 0 ? (
            <div className="space-y-1.5">
              <div className="text-[10px] text-hg-text-dim font-sans uppercase tracking-wider">Target Channels</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {channels.map((ch) => {
                  const Icon = ch.service === 'instagram' ? Instagram :
                                ch.service === 'facebook' ? Facebook :
                                ch.service === 'linkedin' ? Linkedin :
                                ch.service === 'twitter' ? Twitter : Send;
                  const checked = selectedChannels.includes(ch.id);
                  return (
                    <button
                      key={ch.id}
                      data-testid={`schedule-channel-${index}-${ch.id}`}
                      title={`${ch.name} · ${ch.service}`}
                      onClick={() => toggleChannel(ch.id)}
                      className={`flex items-center gap-2 px-3 py-2 text-xs font-sans rounded-sm border transition-colors text-left ${
                        checked ? 'border-hg-green bg-hg-green/10 text-hg-text' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
                      }`}
                    >
                      <Icon size={12} className={checked ? 'text-hg-green' : 'text-hg-text-dim'} strokeWidth={1.5} />
                      <span className="truncate flex-1">{ch.name}</span>
                      {checked && <Check size={12} className="text-hg-green flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-hg-text-dim font-sans">{selectedChannels.length} channel{selectedChannels.length === 1 ? '' : 's'} selected</div>
            </div>
          ) : (
            <div className="text-[11px] text-yellow-400 font-sans">No Buffer channels detected. Check Settings.</div>
          )}

          <div className="flex gap-3">
            <input
              data-testid={`schedule-date-${index}`}
              type="date"
              value={schedDate}
              onChange={(e) => setSchedDate(e.target.value)}
              className="flex-1 bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
            />
            <input
              data-testid={`schedule-time-${index}`}
              type="time"
              value={schedTime}
              onChange={(e) => setSchedTime(e.target.value)}
              className="w-32 bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid={`schedule-confirm-${index}`}
              onClick={handleSchedule}
              disabled={!schedDate || selectedChannels.length === 0 || posting}
              className="bg-hg-green text-hg-text text-xs font-sans font-medium py-2 rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
            >
              {posting ? <Loader2 size={12} className="animate-spin" /> : <Clock size={12} />} Schedule
            </button>
            <button
              data-testid={`post-now-confirm-${index}`}
              onClick={handlePostNow}
              disabled={selectedChannels.length === 0 || posting}
              className="border border-hg-green text-hg-green text-xs font-sans font-medium py-2 rounded-sm hover:bg-hg-green/10 transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
            >
              {posting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Post Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryTab({ channels = [] }) {
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchHistory = async (s, p) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (s) params.append('search', s);
      if (p) params.append('platform', p);
      const res = await axios.get(`${API}/social/history?${params}`);
      setPosts(res.data.posts);
    } catch (e) {
      console.error('Failed to load history', e);
    }
    setLoading(false);
  };

  useState(() => { fetchHistory('', ''); });

  const handleSearch = (val) => {
    setSearch(val);
    fetchHistory(val, platformFilter);
  };

  const handlePlatformFilter = (val) => {
    setPlatformFilter(val);
    fetchHistory(search, val);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-hg-text-dim" strokeWidth={1.5} />
          <input
            data-testid="history-search"
            placeholder="Search posts..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full bg-hg-bg border border-hg-border rounded-sm pl-9 pr-3 py-2 text-sm text-hg-text font-sans placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green"
          />
        </div>
        <select
          data-testid="history-platform-filter"
          value={platformFilter}
          onChange={(e) => handlePlatformFilter(e.target.value)}
          className="bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
        >
          <option value="">All Platforms</option>
          <option value="instagram">Instagram</option>
          <option value="facebook">Facebook</option>
        </select>
      </div>

      {loading && <div className="text-center py-8 text-hg-text-dim text-sm">Loading history...</div>}

      {!loading && posts.length === 0 && (
        <div className="text-center py-12 text-hg-text-dim text-sm">No approved posts yet. Generate and approve posts to see them here.</div>
      )}

      <div className="space-y-3">
        {posts.map((post, i) => {
          const bufferStatus = post.buffer_status;
          const channel = post.buffer_channel_id ? (channels.find((c) => c.id === post.buffer_channel_id) || null) : null;
          const scheduledDate = post.scheduled_at ? new Date(post.scheduled_at) : null;
          return (
            <div key={post.id || i} data-testid={`social-history-row-${i}`} className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs text-hg-text-dim font-sans">
                  {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <span className="flex items-center gap-1 text-xs text-hg-green font-sans">
                  {post.platform === 'instagram' ? <Instagram size={12} /> : <Facebook size={12} />}
                  {post.platform}
                </span>
                <span className="text-xs text-hg-text-dim font-sans uppercase tracking-wider">{post.angle}</span>
                {bufferStatus === 'scheduled' && (
                  <span data-testid={`social-history-status-${i}`} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-sm font-sans bg-hg-green/10 text-hg-green">
                    <Clock size={10} /> Scheduled
                    {scheduledDate && ` · ${scheduledDate.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
                    {channel && ` → ${channel.name}`}
                  </span>
                )}
                {bufferStatus === 'posted' && (
                  <span data-testid={`social-history-status-${i}`} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-sm font-sans bg-hg-green/20 text-hg-green">
                    <Check size={10} /> Posted{channel && ` → ${channel.name}`}
                  </span>
                )}
                {!bufferStatus && (
                  <span className="text-[10px] px-2 py-0.5 rounded-sm font-sans bg-hg-border/40 text-hg-text-dim">Draft</span>
                )}
              </div>
              {post.image_data && (
                <img src={post.image_data} alt="Post" className="w-full h-40 object-cover rounded-sm border border-hg-border" />
              )}
              <p className="text-xs text-hg-text-dim font-sans">Topic: {post.topic}</p>
              <p className="text-sm text-hg-text font-sans whitespace-pre-line">{post.copy}</p>
              <button
                onClick={() => { navigator.clipboard.writeText(post.copy); }}
                className="flex items-center gap-1 text-xs text-hg-text-dim hover:text-hg-green transition-colors font-sans"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SocialStudio() {
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [angle, setAngle] = useState(ANGLES[0]);
  const [audience, setAudience] = useState('mixed');
  const [storyShape, setStoryShape] = useState('auto');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // The dashboard's "Social — N posts awaiting your review" row deep-links to
  // `?module=social&tab=drafts`. Honouring the param at mount is what makes that link land
  // where it says it lands; without it the row opened Social Studio on "Generate Posts".
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      return ['generate', 'history', 'drafts'].includes(t) ? t : 'generate';
    } catch { return 'generate'; }
  });
  const [notification, setNotification] = useState('');
  const [channels, setChannels] = useState([]);

  // Fetch Buffer channels on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/buffer/channels`);
        if (!cancelled) setChannels(res.data.channels || []);
      } catch {
        // Buffer not configured / unreachable — leave channels empty
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(''), 3000);
  };

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    setLoading(true);
    setError('');
    setPosts([]);
    try {
      const res = await axios.post(`${API}/social/generate`, {
        topic, platform, angle,
        audience, story_shape: storyShape,
      });
      setPosts(res.data.posts || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to generate posts. Please try again.');
    }
    setLoading(false);
  };

  const handleSave = async (post, imageData) => {
    try {
      await axios.post(`${API}/social/save`, {
        platform, angle: post.angle, topic, copy: post.copy, note: post.note,
        image_data: imageData || null,
      });
      showNotification('Post approved and saved to history');
    } catch (e) {
      console.error('Save failed', e);
    }
  };

  const handleSchedule = async (post, date, time, imageData, channelIds) => {
    try {
      // First save the post once
      const saveRes = await axios.post(`${API}/social/save`, {
        platform, angle: post.angle, topic, copy: post.copy, note: post.note,
        image_data: imageData || null,
      });
      const postId = saveRes.data.post?.id;
      if (!postId) {
        showNotification('Save failed');
        return;
      }
      // Schedule to every selected channel in parallel
      const results = await Promise.allSettled(
        (channelIds || []).map((chId) =>
          axios.post(`${API}/social/schedule-buffer`, {
            post_id: postId, platform, copy: post.copy,
            scheduled_date: date, scheduled_time: time,
            channel_id: chId,
          })
        )
      );
      const okCount = results.filter((r) => r.status === 'fulfilled').length;
      const failCount = results.length - okCount;
      if (failCount === 0) {
        showNotification(`Scheduled to ${okCount} channel${okCount === 1 ? '' : 's'}`);
      } else {
        const firstErr = results.find((r) => r.status === 'rejected');
        showNotification(`${okCount} scheduled · ${failCount} failed (${firstErr?.reason?.response?.data?.detail || 'error'})`);
      }
    } catch (e) {
      console.error('Schedule failed', e);
      showNotification('Schedule failed');
    }
  };

  const handlePostNow = async (post, imageData, channelIds) => {
    try {
      const saveRes = await axios.post(`${API}/social/save`, {
        platform, angle: post.angle, topic, copy: post.copy, note: post.note,
        image_data: imageData || null,
      });
      const postId = saveRes.data.post?.id;
      if (!postId) {
        showNotification('Save failed');
        return;
      }
      const results = await Promise.allSettled(
        (channelIds || []).map((chId) =>
          axios.post(`${API}/buffer/post-now`, { post_id: postId, channel_id: chId })
        )
      );
      const okCount = results.filter((r) => r.status === 'fulfilled').length;
      const failCount = results.length - okCount;
      if (failCount === 0) {
        showNotification(`Posted now to ${okCount} channel${okCount === 1 ? '' : 's'}`);
      } else {
        const firstErr = results.find((r) => r.status === 'rejected');
        showNotification(`${okCount} posted · ${failCount} failed (${firstErr?.reason?.response?.data?.detail || 'error'})`);
      }
    } catch (e) {
      console.error('Post-now failed', e);
      showNotification('Post failed');
    }
  };

  return (
    <div className="space-y-6">
      {notification && (
        <div data-testid="social-notification" className="fixed top-4 right-4 z-50 bg-hg-surface border border-hg-green/30 rounded-sm px-4 py-3 text-sm text-hg-green font-sans flex items-center gap-2 animate-fade-in">
          <Check size={14} /> {notification}
        </div>
      )}

      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">SOCIAL STUDIO</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">Generate, review & approve Instagram and Facebook posts powered by AI.</p>
      </div>

      <div className="flex gap-2 border-b border-hg-border">
        <button
          data-testid="tab-drafts"
          onClick={() => setActiveTab('drafts')}
          className={`px-4 py-2.5 text-sm font-sans transition-colors border-b-2 -mb-px ${
            activeTab === 'drafts' ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
          }`}
        >
          Drafts
        </button>
        <button
          data-testid="tab-generate"
          onClick={() => setActiveTab('generate')}
          className={`px-4 py-2.5 text-sm font-sans transition-colors border-b-2 -mb-px ${
            activeTab === 'generate' ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
          }`}
        >
          Generate Posts
        </button>
        <button
          data-testid="tab-history"
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2.5 text-sm font-sans transition-colors border-b-2 -mb-px ${
            activeTab === 'history' ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
          }`}
        >
          History
        </button>
      </div>

      {activeTab === 'drafts' ? (
        <SocialDrafts />
      ) : activeTab === 'generate' ? (
        <div className="space-y-6">
          <div className="bg-hg-surface border border-hg-border rounded-sm p-6 space-y-5">
            <div>
              <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Topic</label>
              <textarea
                data-testid="social-topic-input"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="E.g. New Fly Series driver, a fitting insight, upcoming event, Tour news..."
                className="w-full bg-hg-bg border border-hg-border rounded-sm p-3 text-sm text-hg-text font-sans placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
                rows={3}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Platform</label>
                <div className="flex gap-2">
                  {['instagram', 'facebook'].map((p) => (
                    <button
                      key={p}
                      data-testid={`platform-${p}`}
                      onClick={() => setPlatform(p)}
                      className={`flex items-center gap-2 px-4 py-2 text-sm font-sans rounded-sm border transition-colors ${
                        platform === p
                          ? 'border-hg-green bg-hg-green/10 text-hg-green'
                          : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
                      }`}
                    >
                      {p === 'instagram' ? <Instagram size={14} strokeWidth={1.5} /> : <Facebook size={14} strokeWidth={1.5} />}
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1">
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Angle</label>
                <select
                  data-testid="social-angle-select"
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
                >
                  {ANGLES.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Audience</label>
                <select
                  data-testid="social-audience-select"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
                >
                  {AUDIENCE_OPTIONS.map((a) => (
                    <option key={a.id} value={a.id}>{a.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Story shape</label>
                <select
                  data-testid="social-story-shape-select"
                  value={storyShape}
                  onChange={(e) => setStoryShape(e.target.value)}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
                >
                  {STORY_SHAPE_OPTIONS.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              data-testid="generate-post-button"
              onClick={handleGenerate}
              disabled={loading || !topic.trim()}
              className="flex items-center gap-2 bg-hg-green text-hg-text font-sans font-medium text-sm px-5 py-2.5 rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {loading ? 'Generating...' : 'Generate Posts'}
            </button>
          </div>

          {error && (
            <div data-testid="social-error" className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-sm text-red-400 font-sans flex items-center gap-2">
              <X size={14} /> {error}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12 gap-3">
              <div className="w-2 h-2 rounded-full bg-hg-green animate-pulse-green" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 rounded-full bg-hg-green animate-pulse-green" style={{ animationDelay: '300ms' }} />
              <div className="w-2 h-2 rounded-full bg-hg-green animate-pulse-green" style={{ animationDelay: '600ms' }} />
            </div>
          )}

          {posts.length > 0 && (
            <div className="space-y-4">
              <div className="text-xs font-sans text-hg-text-dim uppercase tracking-wider">Generated Posts — add images below</div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {posts.map((post, i) => (
                  <PostCard
                    key={i}
                    post={post}
                    index={i}
                    topic={topic}
                    platform={platform}
                    channels={channels}
                    onSave={handleSave}
                    onCopy={() => showNotification('Post copied to clipboard')}
                    onSchedule={handleSchedule}
                    onPostNow={handlePostNow}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <HistoryTab channels={channels} />
      )}
    </div>
  );
}
